import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronRight, Loader2, X } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import {
  filterSearchableSelectOptions,
  SearchableSelect,
  type SearchableSelectOption,
} from "@/components/ui/SearchableSelect";
import { CardTitleWithHelp, HoverHintContent, PageTitleWithHelp } from "@/components/ui/title-help";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { cn } from "@/lib/utils";
import { useHubToast } from "@/components/ui/HubToast";
import { InventoryAmlMpnEntry } from "@/components/inventory/InventoryAmlMpnEntry";
import { InventoryBoxUnitKindSelect } from "@/components/inventory/InventoryBoxUnitKindSelect";
import {
  InventoryBoxConsumeCard,
  InventoryBoxReleaseToFloorCard,
} from "@/components/inventory/InventoryBoxConsumeReleaseCards";
import { InventoryBoxSplitCombineModal } from "@/components/inventory/InventoryBoxTransferCards";
import { HubUserAvatar } from "@/components/HubUserAvatar";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { MrpSkuHoverPeek, useDeferredMrpSkuHoverHints } from "@/components/mrp/MrpSkuHoverPeek";
import { InboundZplPrinterHeaderButton } from "@/pages/shipping/InboundZplPrinterHeaderButton";
import { resolveInventoryApprovedComponentId } from "@/lib/inventoryAmlMpn";
import { ApiError } from "@/lib/api";
import { useFloorPageScanCapture } from "@/lib/floorPageScanCapture";
import { skuPickerDescriptionLine, skuPickerTitleLine } from "@/lib/mrpSkuPickerDisplay";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";
import { useInboundZplPrinter } from "@/pages/shipping/inboundZplPrinter";
import {
  defaultCarveContainerUnitKind,
  parseInboundSubcontainerUnitKind,
  type InboundArrivalUnitKind,
} from "@/pages/shipping/inboundArrivalUnitKind";
import { fetchInventoryBox, fetchInventoryBoxEvents, reprintInventoryBox, type InventoryBox, type InventoryBoxEvent } from "@/lib/inventoryBoxApi";
import { inventoryBoxCreatedHistoryParts, inventoryBoxHistoryQuantity } from "@/lib/inventoryBoxEventDisplay";
import { WarehouseRecountLocationPanel } from "@/pages/warehouse/WarehouseRecountLocationPanel";
import {
  WarehouseRecountShelfField,
  WarehouseRecountShelfList,
  warehouseRecountShelfDraftToStorageKey,
  warehouseRecountShelfStorageKeyToDraft,
} from "@/pages/warehouse/WarehouseRecountShelfField";
import { parseWarehouseRecountScan } from "@/pages/warehouse/warehouseRecountScan";
import {
  postWarehouseRecountLabelSave,
  warehouseRecountLabelSaveSku,
  warehouseRecountOnHandCardPartNumber,
} from "@/pages/warehouse/warehouseRecountLabelSave";
import {
  carveWarehouseBox,
  fetchWarehouseLocations,
  fetchWarehouseRecountContext,
  printWarehouseRecountLocationLabel,
  saveWarehouseBoxRecount,
  searchWarehouseSkus,
  type WarehouseRecountContext,
  type WarehouseRecountMpnBreakdownRow,
  type WarehouseRecountMpnContainerLine,
} from "@/lib/warehouseRecountApi";

const SGS_HUB_LOCATION_ID = 1;

function ContainerEventTable(props: {
  loading: boolean;
  events: readonly InventoryBoxEvent[];
}): JSX.Element {
  const columns: HubDataTableColumn<InventoryBoxEvent, "when" | "event" | "quantity" | "detail" | "by">[] = [
    {
      id: "when",
      header: "When",
      sortValue: (event) => event.created_at,
      overflow: "nowrap",
      headerClassName: "w-36",
      cellClassName: "w-36",
      cell: (event) => {
        const when = new Date(event.created_at);
        return (
          <span className="block leading-tight">
            <span className="block">{when.toLocaleDateString()}</span>
            <span className="block text-slate-500">{when.toLocaleTimeString()}</span>
          </span>
        );
      },
    },
    {
      id: "event",
      header: "Event",
      sortValue: (event) => inventoryBoxCreatedHistoryParts(event)?.event ?? event.event_kind,
      overflow: "nowrap",
      headerClassName: "w-28",
      cellClassName: "w-28",
      cell: (event) =>
        inventoryBoxCreatedHistoryParts(event)?.event ?? event.event_kind.replaceAll("_", " "),
    },
    {
      id: "quantity",
      header: "Quantity",
      align: "right",
      sortValue: (event) => event.quantity_after ?? event.quantity_before,
      overflow: "nowrap",
      headerClassName: "w-28",
      cellClassName: "w-28",
      cell: (event) => inventoryBoxHistoryQuantity(event),
    },
    {
      id: "detail",
      header: "Detail",
      sortValue: (event) => inventoryBoxCreatedHistoryParts(event)?.detail ?? event.detail ?? "",
      overflow: "wrap",
      cell: (event) => inventoryBoxCreatedHistoryParts(event)?.detail ?? event.detail ?? "",
    },
    {
      id: "by",
      header: "By",
      sortValue: (event) => event.actor_display_name ?? "",
      overflow: "nowrap",
      headerClassName: "w-52",
      cellClassName: "w-52",
      cell: (event) => {
        const label = event.actor_display_name?.trim() ?? "";
        if (label === "") return "";
        return (
          <span className="inline-flex items-center gap-2">
            <HubUserAvatar
              avatarUrl={event.actor_avatar_url}
              label={label}
              size="sm"
              className="h-5 w-5 text-[10px]"
            />
            {label}
          </span>
        );
      },
    },
  ];
  return (
    <HubDataTable
      columns={columns}
      rows={props.events}
      rowKey={(event) => event.id}
      fillViewport={false}
      defaultSortKey="when"
      defaultSortDir="desc"
      loading={props.loading && props.events.length === 0}
      loadingMessage="Loading history."
      empty={<span className="text-sm text-slate-600">No history for this container.</span>}
      tableClassName="table-fixed"
    />
  );
}

const RECOUNT_HOVER_HINT =
  "Recount a SKU at a warehouse location. Scan a container to change its quantity and Megaventory on-hand together. "
  + "Without a container, carve a new label from unclaimed bulk (on-hand minus labeled containers). "
  + "A quantity above unclaimed inventory at SGS opens an inventory discrepancy for the extra and still prints the label.";

const LOCATION_LABEL_HOVER_HINT =
  "Prints the 4x2 SKU location tag for the selected warehouse. The Data Matrix opens inventory adjust for that SKU and location. Quantity is not printed.";

const WAREHOUSE_RECOUNT_QUERY_KEY = "warehouse-recount";
const SKU_SEARCH_DEBOUNCE_MS = 800;
const SKU_SEARCH_MIN_CHARS = 2;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

function formatQuantity(value: number): string {
  return value.toLocaleString();
}

function findWarehouseRecountContainerInBreakdown(
  breakdown: readonly WarehouseRecountMpnBreakdownRow[],
  hex: string,
): { line: WarehouseRecountMpnContainerLine; mpnRow: WarehouseRecountMpnBreakdownRow } | null {
  for (const mpnRow of breakdown) {
    const line = mpnRow.containers.find((container) => container.hex === hex);
    if (line != null) {
      return { line, mpnRow };
    }
  }
  return null;
}

export function WarehouseRecountPage(): JSX.Element {
  usePageBreadcrumb("Recount");
  const [params] = useSearchParams();
  const sku = params.get("sku") ?? "";
  const locationRaw = params.get("location");
  const parsedLocation = locationRaw == null ? Number.NaN : Number.parseInt(locationRaw, 10);
  return (
    <WarehouseRecountWorkspace
      initialSku={sku}
      initialHubLocationId={Number.isFinite(parsedLocation) && parsedLocation > 0 ? parsedLocation : null}
    />
  );
}

export function WarehouseRecountWorkspace({
  embedded = false,
  initialSku = "",
  initialHubLocationId = null,
}: {
  embedded?: boolean;
  initialSku?: string;
  /** From /warehouse/recount?location= when location-label redirects here. */
  initialHubLocationId?: number | null;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const scanRef = useRef<HTMLInputElement | null>(null);
  const { station, setStation, selectedPrinter, unavailableMessage } = useInboundZplPrinter();

  const [skuDraft, setSkuDraft] = useState(initialSku.trim());
  const [skuSearch, setSkuSearch] = useState("");
  const [hubLocationId, setHubLocationId] = useState<number | null>(initialHubLocationId);
  const [showNonPrimarySites, setShowNonPrimarySites] = useState(false);
  const [boxHex, setBoxHex] = useState<string | null>(null);
  const [boxShortCode, setBoxShortCode] = useState<string | null>(null);
  const [boxQuantityDraft, setBoxQuantityDraft] = useState("");
  const [carveQuantityDraft, setCarveQuantityDraft] = useState("");
  const [shelfDraft, setShelfDraft] = useState("");
  const [approvedComponentIdDraft, setApprovedComponentIdDraft] = useState<string | null>(null);
  const [unitKindDraft, setUnitKindDraft] = useState<InboundArrivalUnitKind>("container");
  const [scanError, setScanError] = useState<string | null>(null);
  const [boxLookupPending, setBoxLookupPending] = useState(false);
  const [recountOpen, setRecountOpen] = useState(false);
  const [recountQuantityDraft, setRecountQuantityDraft] = useState("");
  const [recountReason, setRecountReason] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [transferMode, setTransferMode] = useState<"split" | "combine" | null>(null);
  const [transferBox, setTransferBox] = useState<InventoryBox | null>(null);
  const [cartonAction, setCartonAction] = useState<"consume" | "release" | null>(null);
  const [cartonActionBox, setCartonActionBox] = useState<InventoryBox | null>(null);
  const [cartonActionPending, setCartonActionPending] = useState(false);
  const boxLookupInFlightRef = useRef(false);
  const carveUnitKindAppliedRef = useRef("");

  const locationsQuery = useQuery({
    queryKey: ["warehouse", "locations"],
    queryFn: fetchWarehouseLocations,
  });

  const debouncedSkuSearch = useDebouncedValue(skuSearch.trim(), SKU_SEARCH_DEBOUNCE_MS);

  const skuSearchQuery = useQuery({
    queryKey: ["warehouse", "sku-search", "sku-only", debouncedSkuSearch],
    queryFn: ({ signal }) => searchWarehouseSkus(debouncedSkuSearch, signal),
    enabled: debouncedSkuSearch.length >= SKU_SEARCH_MIN_CHARS && debouncedSkuSearch === skuSearch.trim(),
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const skuOptions = useMemo((): SearchableSelectOption[] => {
    return (skuSearchQuery.data ?? []).map((row) => {
      const descriptionLine = skuPickerDescriptionLine(row);
      return {
        value: row.sku,
        label: skuPickerTitleLine(row),
        description: descriptionLine.length > 0 ? descriptionLine : null,
      };
    });
  }, [skuSearchQuery.data]);

  const selectedSkuOption = useMemo((): SearchableSelectOption | null => {
    const trimmed = skuDraft.trim();
    if (trimmed === "") return null;
    const match = skuOptions.find((option) => option.value === trimmed);
    return match ?? { value: trimmed, label: trimmed, description: null };
  }, [skuDraft, skuOptions]);

  const lookupActive = skuDraft.trim() !== "" || boxHex != null;

  const clearItemLookup = () => {
    setSkuDraft("");
    setSkuSearch("");
    setBoxHex(null);
    setBoxShortCode(null);
    setHubLocationId(null);
    setCarveQuantityDraft("");
    setShelfDraft("");
    setUnitKindDraft("container");
    setScanError(null);
  };

  const dismissItemLookupSearchField = () => {
    setSkuSearch("");
    scanRef.current?.blur();
  };

  const baseContextQuery = useQuery({
    queryKey: [WAREHOUSE_RECOUNT_QUERY_KEY, "base", skuDraft],
    queryFn: () =>
      fetchWarehouseRecountContext({
        sku: skuDraft,
        hubLocationId: null,
      }),
    enabled: skuDraft.trim() !== "",
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const siteContextQuery = useQuery({
    queryKey: [WAREHOUSE_RECOUNT_QUERY_KEY, "site", skuDraft, hubLocationId],
    queryFn: () =>
      fetchWarehouseRecountContext({
        sku: skuDraft,
        hubLocationId,
      }),
    enabled: skuDraft.trim() !== "" && hubLocationId != null,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const baseFetched = baseContextQuery.data ?? null;
  const baseContext =
    warehouseRecountOnHandCardPartNumber({
      committedSku: skuDraft,
      fetchedPartNumber: baseFetched?.sku,
    }) != null
      ? baseFetched
      : null;
  const siteFetched = siteContextQuery.data ?? null;
  const siteContext =
    warehouseRecountOnHandCardPartNumber({
      committedSku: skuDraft,
      fetchedPartNumber: siteFetched?.sku,
    }) != null
      ? siteFetched
      : null;

  const context = useMemo(() => {
    if (baseContext == null) {
      return null;
    }
    if (hubLocationId == null) {
      return baseContext;
    }
    if (siteContext != null && siteContext.hub_location_id === hubLocationId) {
      return siteContext;
    }
    const summaryRow = baseContext.sku_locations.find(
      (row) => row.hub_location_id === hubLocationId,
    );
    if (summaryRow == null) {
      return baseContext;
    }
    return {
      ...baseContext,
      hub_location_id: hubLocationId,
      location_name: summaryRow.location_name,
      on_hand: summaryRow.on_hand,
      boxed_total: summaryRow.boxed_total,
      transient_quantity: summaryRow.transient_quantity,
      unclaimed: summaryRow.unclaimed,
      megaventory_location_id: siteContext?.megaventory_location_id ?? null,
      shelf_sublocations: siteContext?.shelf_sublocations ?? [],
      mpn_breakdown: siteContext?.mpn_breakdown?.length
        ? siteContext.mpn_breakdown
        : summaryRow.mpn_breakdown ?? [],
      box: siteContext?.box ?? baseContext.box,
    };
  }, [baseContext, hubLocationId, siteContext]);

  const effectiveHubLocationId = hubLocationId ?? context?.hub_location_id ?? null;
  const selectedBreakdown = context?.sku_locations.find(
    (row) => row.hub_location_id === effectiveHubLocationId,
  )?.mpn_breakdown ?? context?.mpn_breakdown ?? [];
  const siteDetailLoading =
    hubLocationId != null
    && selectedBreakdown.length === 0
    && siteContextQuery.isFetching
    && (siteContext == null || siteContext.hub_location_id !== hubLocationId);
  const warehouseSelected = effectiveHubLocationId != null;

  useEffect(() => {
    if (boxHex != null) return;
    setApprovedComponentIdDraft(null);
  }, [boxHex]);

  useEffect(() => {
    if (context?.box != null) {
      setBoxQuantityDraft(String(context.box.quantity));
      setApprovedComponentIdDraft(
        resolveInventoryApprovedComponentId({
          alternates: context.aml_mpn_alternates,
          savedApprovedComponentId: context.box.approved_component_id,
          draftApprovedComponentId: null,
        }),
      );
    } else if (context != null) {
      setApprovedComponentIdDraft(null);
    }
  }, [context?.box?.hex, context?.box?.quantity, context?.box?.approved_component_id, context?.aml_mpn_alternates]);

  useEffect(() => {
    if (context?.box != null) {
      setShelfDraft(warehouseRecountShelfStorageKeyToDraft(context.box.sublocation));
      setUnitKindDraft(parseInboundSubcontainerUnitKind(context.box.unit_kind));
    }
  }, [context?.box?.hex, context?.box?.sublocation, context?.box?.unit_kind]);

  const carveUnitKindsInUse = useMemo(
    () =>
      (context?.mpn_breakdown ?? []).flatMap((row) =>
        (row.containers ?? []).map((line) => line.unit_kind),
      ),
    [context?.mpn_breakdown],
  );
  const carveUnitKindToken = `${skuDraft.trim()}|${String(effectiveHubLocationId ?? "")}|${
    context?.box == null ? "carve" : "box"
  }|${carveUnitKindsInUse.join(",")}`;

  useEffect(() => {
    if (context?.box != null) return;
    if (carveUnitKindAppliedRef.current === carveUnitKindToken) return;
    carveUnitKindAppliedRef.current = carveUnitKindToken;
    setUnitKindDraft(defaultCarveContainerUnitKind(carveUnitKindsInUse));
  }, [carveUnitKindToken, carveUnitKindsInUse, context?.box]);

  const rememberWarehouseSummary = (
    source: WarehouseRecountContext,
    siteHubLocationId: number,
  ) => {
    const sku = source.sku.trim();
    const summary: WarehouseRecountContext = { ...source, box: null, box_badges: [] };
    queryClient.setQueryData(
      [WAREHOUSE_RECOUNT_QUERY_KEY, "site", sku, siteHubLocationId, null],
      summary,
    );
    queryClient.setQueryData<WarehouseRecountContext | undefined>(
      [WAREHOUSE_RECOUNT_QUERY_KEY, "base", sku, null],
      (current) => {
        if (current == null) return summary;
        return {
          ...current,
          sku_locations: source.sku_locations,
          aml_mpn_alternates: source.aml_mpn_alternates,
        };
      },
    );
  };

  const patchRecountContextFromServer = (
    nextContext: WarehouseRecountContext,
    nextBoxHex: string | null,
  ) => {
    const sku = nextContext.sku.trim();
    queryClient.setQueryData(
      [WAREHOUSE_RECOUNT_QUERY_KEY, "base", sku, nextBoxHex],
      nextContext,
    );
    const siteHubLocationId = nextContext.hub_location_id ?? hubLocationId;
    if (siteHubLocationId != null) {
      queryClient.setQueryData(
        [WAREHOUSE_RECOUNT_QUERY_KEY, "site", sku, siteHubLocationId, nextBoxHex],
        nextContext,
      );
      rememberWarehouseSummary(nextContext, siteHubLocationId);
    }
  };

  const prefetchRecountContextForBox = async (
    sku: string,
    hex: string,
    siteHubId: number | null,
  ) => {
    const baseCtx = await fetchWarehouseRecountContext({
      sku,
      hubLocationId: null,
      boxHex: hex,
    });
    patchRecountContextFromServer(baseCtx, hex);
    const resolvedSiteHub = siteHubId ?? baseCtx.hub_location_id ?? hubLocationId;
    if (resolvedSiteHub != null) {
      const siteCtx = await fetchWarehouseRecountContext({
        sku,
        hubLocationId: resolvedSiteHub,
        boxHex: hex,
      });
      patchRecountContextFromServer(siteCtx, hex);
    }
    return baseCtx;
  };

  const applyKnownContainerLineToDrafts = (
    line: WarehouseRecountMpnContainerLine,
    mpnRow: WarehouseRecountMpnBreakdownRow,
  ) => {
    setBoxShortCode(line.short_code);
    setBoxQuantityDraft(String(line.quantity));
    const shelfLabel = line.sublocation_label.trim();
    setShelfDraft(
      shelfLabel === "" || shelfLabel === "No sub location"
        ? ""
        : warehouseRecountShelfStorageKeyToDraft(shelfLabel),
    );
    setUnitKindDraft(parseInboundSubcontainerUnitKind(line.unit_kind));
    setApprovedComponentIdDraft(
      resolveInventoryApprovedComponentId({
        alternates: context?.aml_mpn_alternates ?? [],
        savedApprovedComponentId: mpnRow.approved_component_id,
        draftApprovedComponentId: approvedComponentIdDraft,
      }),
    );
  };

  const syncDraftsFromContextBox = (nextContext: WarehouseRecountContext) => {
    if (nextContext.box == null) {
      return;
    }
    setBoxShortCode(nextContext.box.short_code);
    setBoxQuantityDraft(String(nextContext.box.quantity));
    setShelfDraft(warehouseRecountShelfStorageKeyToDraft(nextContext.box.sublocation));
    setUnitKindDraft(parseInboundSubcontainerUnitKind(nextContext.box.unit_kind));
    setApprovedComponentIdDraft(
      resolveInventoryApprovedComponentId({
        alternates: nextContext.aml_mpn_alternates,
        savedApprovedComponentId: nextContext.box.approved_component_id,
        draftApprovedComponentId: null,
      }),
    );
  };

  const handleHubLocationChange = (nextHubLocationId: number) => {
    if (boxHex != null && context != null) {
      const sourceHub = context.hub_location_id ?? hubLocationId;
      if (sourceHub != null) {
        rememberWarehouseSummary(context, sourceHub);
      }
    }
    setHubLocationId(nextHubLocationId);
    if (boxHex != null) {
      setBoxHex(null);
      setBoxShortCode(null);
    }
    if (nextHubLocationId !== hubLocationId) {
      setShelfDraft("");
    }
  };

  const loadSku = (
    sku: string,
    nextBoxHex: string | null = null,
    options?: { keepWarehouse?: boolean },
  ) => {
    setScanError(null);
    const trimmed = sku.trim();
    setSkuDraft(trimmed);
    setBoxHex(nextBoxHex);
    if (nextBoxHex == null) {
      setBoxShortCode(null);
      setUnitKindDraft("container");
    }
    dismissItemLookupSearchField();
    if (!options?.keepWarehouse && nextBoxHex == null) {
      setHubLocationId(null);
    }
    setCarveQuantityDraft("");
    setShelfDraft("");
  };

  const beginContainerLookup = (raw: string, hex: string) => {
    if (boxLookupInFlightRef.current) {
      return;
    }
    setScanError(null);
    setSkuSearch(raw.trim());
    void selectRecountBox(hex);
  };

  const selectRecountBox = async (hex: string) => {
    const normalizedHex = hex.trim();
    if (normalizedHex === "") {
      return;
    }
    if (boxLookupInFlightRef.current) {
      return;
    }
    if (boxHex === normalizedHex) {
      return;
    }

    const sku = skuDraft.trim();
    const breakdowns = [
      ...(context?.mpn_breakdown ?? []),
      ...(context?.sku_locations ?? []).flatMap((row) => row.mpn_breakdown ?? []),
    ];
    const knownContainer =
      sku !== "" && context != null && context.sku.trim() === sku
        ? findWarehouseRecountContainerInBreakdown(breakdowns, normalizedHex)
        : null;

    if (knownContainer != null) {
      setScanError(null);
      setCarveQuantityDraft("");
      setBoxHex(normalizedHex);
      applyKnownContainerLineToDrafts(knownContainer.line, knownContainer.mpnRow);
      dismissItemLookupSearchField();
      return;
    }

    boxLookupInFlightRef.current = true;
    setBoxLookupPending(true);
    try {
      const box = await fetchInventoryBox(normalizedHex);
      if (box.location_id != null) {
        setHubLocationId(box.location_id);
      }
      setScanError(null);
      const trimmedSku = box.sku.trim();
      setSkuDraft(trimmedSku);
      setBoxHex(normalizedHex);
      setBoxShortCode(box.short_code);
      setCarveQuantityDraft("");
      setShelfDraft(box.sublocation.trim() === "" ? "" : box.sublocation);
      dismissItemLookupSearchField();
      const hubForPrefetch = box.location_id ?? effectiveHubLocationId;
      const nextContext = await prefetchRecountContextForBox(trimmedSku, normalizedHex, hubForPrefetch);
      syncDraftsFromContextBox(nextContext);
    } catch (error) {
      setScanError(error instanceof ApiError ? error.message : "That container was not found.");
    } finally {
      boxLookupInFlightRef.current = false;
      setBoxLookupPending(false);
    }
  };

  const handleScan = async (raw: string) => {
    const parsed = parseWarehouseRecountScan(raw);
    if (parsed == null) {
      setScanError("Could not read that scan.");
      return;
    }
    if (parsed.kind === "box") {
      await selectRecountBox(parsed.hex);
      return;
    }
    if (parsed.kind === "location_tag") {
      const locations = locationsQuery.data ?? [];
      const match = locations.find(
        (location) => location.megaventory_location_id === parsed.megaventoryLocationId,
      );
      if (match != null) {
        setHubLocationId(match.hub_location_id);
      }
      loadSku(parsed.sku, null, { keepWarehouse: true });
      return;
    }
    loadSku(parsed.sku, null);
  };

  useFloorPageScanCapture({
    enabled: transferMode == null,
    inputRef: scanRef,
    onScan: (value: string) => {
      void handleScan(value);
    },
  });

  useEffect(() => {
    if (transferMode == null) return;
    scanRef.current?.blur();
  }, [transferMode]);

  useEffect(() => {
    if (boxHex == null) {
      setTransferMode(null);
      setTransferBox(null);
    }
    setCartonAction(null);
    setCartonActionBox(null);
    setHistoryOpen(false);
    setRecountOpen(false);
  }, [boxHex]);

  const containerEventsQuery = useQuery({
    queryKey: ["inventory-box-events", boxHex],
    enabled: boxHex != null && boxHex.trim() !== "",
    queryFn: () => fetchInventoryBoxEvents(boxHex ?? ""),
  });

  const openTransfer = async (mode: "split" | "combine") => {
    let box = context?.box ?? null;
    if ((box == null || box.hex !== boxHex) && boxHex != null) {
      try {
        box = await fetchInventoryBox(boxHex);
      } catch (error) {
        showToast({
          message: error instanceof ApiError ? error.message : "Could not load that container.",
          tone: "error",
        });
        return;
      }
    }
    if (box == null || box.archived_at != null) return;
    setTransferBox(box);
    setTransferMode(mode);
  };

  const openCartonAction = async (kind: "consume" | "release") => {
    if (cartonAction === kind) {
      setCartonAction(null);
      setCartonActionBox(null);
      return;
    }
    let box = context?.box ?? null;
    if ((box == null || box.hex !== boxHex) && boxHex != null) {
      try {
        box = await fetchInventoryBox(boxHex);
      } catch (error) {
        showToast({
          message: error instanceof ApiError ? error.message : "Could not load that container.",
          tone: "error",
        });
        return;
      }
    }
    if (box == null || box.archived_at != null) return;
    setCartonActionBox(box);
    setCartonAction(kind);
  };

  const boxRecountMutation = useMutation({
    mutationFn: (input: {
      quantity: number;
      reason?: string | null;
      unitKind?: string;
      shelf?: string;
      approvedComponentId?: string | null;
    }) => {
      if (boxHex == null || boxHex.trim() === "") {
        throw new Error("Scan a container first.");
      }
      if (effectiveHubLocationId == null) {
        throw new Error("Select a warehouse for this container.");
      }
      if (!Number.isFinite(input.quantity) || input.quantity < 0) {
        throw new Error("Enter a valid container quantity.");
      }
      return saveWarehouseBoxRecount({
        boxHex,
        quantity: input.quantity,
        hubLocationId: effectiveHubLocationId,
        sublocation: warehouseRecountShelfDraftToStorageKey(input.shelf ?? shelfDraft),
        unitKind: input.unitKind ?? unitKindDraft,
        approvedComponentId:
          input.approvedComponentId !== undefined
            ? input.approvedComponentId
            : approvedComponentIdDraft,
        reason: input.reason,
      });
    },
    onSuccess: (result, input) => {
      patchRecountContextFromServer(result.context, result.box.hex);
      setBoxHex(result.box.hex);
      setBoxShortCode(result.box.short_code);
      setBoxQuantityDraft(String(result.box.quantity));
      if (result.box.location_id != null) {
        setHubLocationId(result.box.location_id);
      }
      setShelfDraft(warehouseRecountShelfStorageKeyToDraft(result.box.sublocation));
      setUnitKindDraft(parseInboundSubcontainerUnitKind(result.box.unit_kind));
      if (input.reason != null && input.reason.trim() !== "") {
        setRecountOpen(false);
        setRecountReason("");
        void queryClient.invalidateQueries({ queryKey: ["inventory-box-events", result.box.hex] });
        showToast({ message: `Recount saved at ${String(result.box.quantity)}.`, tone: "success" });
      }
    },
    onError: (error, input) => {
      if (input.shelf != null && context?.box != null) {
        setShelfDraft(warehouseRecountShelfStorageKeyToDraft(context.box.sublocation));
      }
      if (input.approvedComponentId != null && context?.box != null) {
        setApprovedComponentIdDraft(context.box.approved_component_id);
      }
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const commitContainerMpn = (approvedComponentId: string) => {
    setApprovedComponentIdDraft(approvedComponentId);
    const quantity = context?.box?.quantity;
    if (quantity == null || context?.box == null) return;
    const saved = context.box.approved_component_id?.trim() ?? "";
    if (approvedComponentId.trim() === saved) return;
    boxRecountMutation.mutate({ quantity, approvedComponentId });
  };

  const commitContainerShelf = (draft: string) => {
    setShelfDraft(draft);
    const quantity = context?.box?.quantity;
    if (quantity == null || context?.box == null) return;
    const nextKey = warehouseRecountShelfDraftToStorageKey(draft);
    const savedKey = warehouseRecountShelfDraftToStorageKey(context.box.sublocation ?? "");
    if (nextKey === savedKey) return;
    boxRecountMutation.mutate({ quantity, shelf: draft });
  };

  const carveMutation = useMutation({
    mutationFn: async () => {
      const quantity = Number.parseInt(carveQuantityDraft.trim(), 10);
      if (!Number.isFinite(quantity) || quantity <= 0) {
        throw new Error("Enter a positive quantity to carve.");
      }
      const printer = selectedPrinter.trim();
      if (printer === "") {
        throw new Error("Pick a label printer first.");
      }
      if (effectiveHubLocationId == null) {
        throw new Error("Select a warehouse from the on-hand summary first.");
      }
      const hubLocationIdForLabel = effectiveHubLocationId;
      const result = await postWarehouseRecountLabelSave({
        searchText: skuSearch,
        cardPartNumber: context?.sku,
        save: "carve",
        post: (sku) =>
          carveWarehouseBox({
            sku,
            hubLocationId: hubLocationIdForLabel,
            quantity,
            printer,
            sublocation: warehouseRecountShelfDraftToStorageKey(shelfDraft),
            unitKind: unitKindDraft,
            approvedComponentId: approvedComponentIdDraft,
          }),
      });
      if (result == null) {
        throw new Error("Search for a part before carving a container.");
      }
      return result;
    },
    onSuccess: (result) => {
      patchRecountContextFromServer(result.context, result.box.hex);
      setBoxHex(result.box.hex);
      setBoxShortCode(result.box.short_code);
      setBoxQuantityDraft(String(result.box.quantity));
      dismissItemLookupSearchField();
      setCarveQuantityDraft("");
      setShelfDraft(warehouseRecountShelfStorageKeyToDraft(result.box.sublocation));
      setUnitKindDraft(parseInboundSubcontainerUnitKind(result.box.unit_kind));
      setApprovedComponentIdDraft(
        resolveInventoryApprovedComponentId({
          alternates: result.context.aml_mpn_alternates,
          savedApprovedComponentId: result.box.approved_component_id,
          draftApprovedComponentId: null,
        }),
      );
      const discrepancy = result.inventory_discrepancy_request;
      const discrepancyNote =
        discrepancy == null
          ? ""
          : ` Inventory discrepancy #${discrepancy.id} opened for +${String(discrepancy.quantity)}.`;
      showToast({
        message: `New container ${result.box.short_code} carved for ${String(result.box.quantity)}.${discrepancyNote}`,
        tone: "success",
      });
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const reprintContainerMutation = useMutation({
    mutationFn: async () => {
      if (boxHex == null || boxHex.trim() === "") {
        throw new Error("Scan a container first.");
      }
      const printer = selectedPrinter.trim();
      if (printer === "") {
        throw new Error("Pick a label printer first.");
      }
      return reprintInventoryBox(boxHex, printer);
    },
    onSuccess: () => {
      showToast({ message: "Container label sent to the printer.", tone: "success" });
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const printLocationMutation = useMutation({
    mutationFn: async () => {
      const printer = selectedPrinter.trim();
      if (printer === "") {
        throw new Error("Pick a label printer first.");
      }
      if (effectiveHubLocationId == null) {
        throw new Error("Select a warehouse from the on-hand summary first.");
      }
      const hubLocationIdForLabel = effectiveHubLocationId;
      const result = await postWarehouseRecountLabelSave({
        searchText: skuSearch,
        cardPartNumber: context?.sku,
        save: "print-location",
        post: (sku) =>
          printWarehouseRecountLocationLabel({
            sku,
            hubLocationId: hubLocationIdForLabel,
            printer,
          }),
      });
      if (result == null) {
        throw new Error("Search for a part before printing a label.");
      }
      return result;
    },
    onSuccess: () => {
      showToast({ message: "Location label sent to the printer.", tone: "success" });
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const parsedCarveQuantity = Number.parseInt(carveQuantityDraft.trim(), 10);
  const carveAtSgs = effectiveHubLocationId === SGS_HUB_LOCATION_ID;
  const carveFoundQuantity =
    context != null && Number.isFinite(parsedCarveQuantity) && parsedCarveQuantity > 0
      ? Math.max(0, parsedCarveQuantity - context.unclaimed)
      : 0;
  const carveExceedsUnclaimedElsewhere = carveFoundQuantity > 0 && !carveAtSgs;
  const carveQuantityValid =
    warehouseSelected
    && Number.isFinite(parsedCarveQuantity)
    && parsedCarveQuantity > 0
    && !carveExceedsUnclaimedElsewhere;
  const carveStatusMessage = context == null
    ? ""
    : carveFoundQuantity > 0 && carveAtSgs
        ? `This quantity is ${formatQuantity(carveFoundQuantity)} above unclaimed inventory. Carving opens an SGS inventory discrepancy for that extra.`
        : context.unclaimed > 0 && carveAtSgs
          ? `Up to ${formatQuantity(context.unclaimed)} unclaimed at SGS without changing on-hand. A higher quantity opens an inventory discrepancy for the extra.`
            : context.unclaimed > 0
              ? `Up to ${formatQuantity(context.unclaimed)} unclaimed at this warehouse without changing on-hand.`
              : carveAtSgs
                ? ""
                : "No unclaimed bulk at this warehouse. Pick another site, scan a container to recount, or adjust on-hand from a location tag.";
  const carveMpnReady =
    context == null
    || context.aml_mpn_alternates.length === 0
    || (approvedComponentIdDraft != null && approvedComponentIdDraft.trim() !== "");
  const skuPeekHints = useDeferredMrpSkuHoverHints(
    context?.sku.trim() ? [context.sku.trim()] : [],
    "warehouse-recount",
  );
  const recountBusy =
    boxRecountMutation.isPending
    || carveMutation.isPending
    || printLocationMutation.isPending
    || reprintContainerMutation.isPending;
  const busy = recountBusy || cartonActionPending;
  const carveFormDisabled =
    busy
    || !warehouseSelected
    || (context != null && context.unclaimed <= 0 && !carveAtSgs);
  const selectedWarehouseMegaventoryLocationId =
    (locationsQuery.data ?? []).find(
      (location) => location.hub_location_id === effectiveHubLocationId,
    )?.megaventory_location_id
    ?? context?.megaventory_location_id
    ?? null;
  const labelSaveSku = warehouseRecountLabelSaveSku({
    searchText: skuSearch,
    cardPartNumber: context?.sku,
  });
  const canPrintLocationLabel =
    labelSaveSku != null
    && warehouseSelected
    && selectedWarehouseMegaventoryLocationId != null
    && selectedPrinter.trim() !== "";
  const canReprintContainerLabel =
    boxHex != null && boxHex.trim() !== "" && selectedPrinter.trim() !== "";

  return (
    <div className="space-y-4">
      {!embedded ? (
        <header className="flex flex-wrap items-start justify-between gap-3">
          <PageTitleWithHelp
            hoverHint={RECOUNT_HOVER_HINT}
            breadcrumbLabel="Recount"
            className="min-w-0 flex-1"
          >
            Recount
          </PageTitleWithHelp>
          <InboundZplPrinterHeaderButton
            className="ml-auto"
            station={station}
            onStationChange={setStation}
            unavailableMessage={unavailableMessage}
          />
        </header>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Item lookup</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Label htmlFor="warehouse-recount-item">Search or scan</Label>
            <SearchableSelect
              id="warehouse-recount-item"
              presentation="inlineFilter"
              commitFirstFilteredOption
              clearable
              clearLabel="Clear search"
              className="text-base"
              floorScanTarget
              searchInputRef={scanRef}
              value={lookupActive ? "" : skuDraft}
              onValueChange={(option) => {
                if (option == null) {
                  if (lookupActive || boxLookupPending) {
                    return;
                  }
                  clearItemLookup();
                  return;
                }
                loadSku(option.value, null);
              }}
              options={skuOptions}
              searchValue={skuSearch}
              disabled={boxLookupPending}
              onSearchChange={(query) => {
                if (boxLookupPending) {
                  return;
                }
                const trimmed = query.trim();
                if (trimmed === "") {
                  // Blur of the scan field also reports an empty query. That must not drop a
                  // loaded SKU. Clear only when the person emptied text that was already in the box.
                  if (lookupActive && skuSearch.trim() !== "") {
                    clearItemLookup();
                  } else {
                    setSkuSearch("");
                  }
                  return;
                }
                const parsedScan = parseWarehouseRecountScan(trimmed);
                if (parsedScan?.kind === "box") {
                  beginContainerLookup(trimmed, parsedScan.hex);
                  return;
                }
                if (lookupActive) {
                  setSkuDraft("");
                  setBoxHex(null);
                  setBoxShortCode(null);
                  setHubLocationId(null);
                  setCarveQuantityDraft("");
                  setShelfDraft("");
                }
                setSkuSearch(query);
              }}
              placeholder="SKU, description, container label, or location tag"
              searchPlaceholder="SKU, description, container label, or location tag"
              emptyMessage={
                boxLookupPending
                  ? "Loading container…"
                  : skuSearchQuery.isFetching && debouncedSkuSearch === skuSearch.trim()
                    ? "Searching…"
                    : "No matching items"
              }
              isLoading={
                boxLookupPending
                || (skuSearchQuery.isFetching && debouncedSkuSearch === skuSearch.trim())
              }
              selectedLabelOverride={lookupActive ? null : (selectedSkuOption?.label ?? null)}
              onSearchInputKeyDown={(event) => {
                if (event.key !== "Enter") return;
                const raw = skuSearch.trim();
                if (raw === "") return;
                const parsedScan = parseWarehouseRecountScan(raw);
                if (parsedScan?.kind === "box") {
                  event.preventDefault();
                  beginContainerLookup(raw, parsedScan.hex);
                  return;
                }
                const searchStillLoading =
                  boxLookupPending
                  || (skuSearchQuery.isFetching && debouncedSkuSearch === skuSearch.trim());
                const hasMatch = filterSearchableSelectOptions(skuOptions, skuSearch)
                  .some((option) => option.disabled !== true);
                if (searchStillLoading && !hasMatch) return;
                if (hasMatch) return;
                if (parsedScan?.kind === "location_tag") {
                  event.preventDefault();
                  setSkuSearch("");
                  void handleScan(raw);
                  return;
                }
                event.preventDefault();
              }}
            />
            {boxLookupPending ? (
              <p className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading container.
              </p>
            ) : null}
            {scanError != null ? (
              <p role="alert" className="mt-2 flex items-center gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4" />
                {scanError}
              </p>
            ) : null}
          </div>
        </CardBody>
      </Card>

      {baseContextQuery.isLoading && skuDraft.trim() !== "" && baseContext == null ? (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading inventory.
        </p>
      ) : null}

      {baseContextQuery.error != null ? (
        <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="h-4 w-4" />
          {baseContextQuery.error instanceof ApiError
            ? baseContextQuery.error.message
            : "Could not load inventory."}
        </p>
      ) : null}

      {siteContextQuery.error != null && hubLocationId != null ? (
        <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="h-4 w-4" />
          {siteContextQuery.error instanceof ApiError
            ? siteContextQuery.error.message
            : "Could not load warehouse details."}
        </p>
      ) : null}

      {context != null ? (
        <>
          <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
            <Card>
              <CardHeader className="flex h-14 flex-nowrap items-center justify-between gap-2 py-0">
                <CardTitle className="min-w-0">On-hand summary</CardTitle>
                <HoverHintContent hoverHint={LOCATION_LABEL_HOVER_HINT}>
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-9 shrink-0"
                    disabled={!warehouseSelected || !canPrintLocationLabel || busy}
                    onClick={() => printLocationMutation.mutate()}
                  >
                    Print location label
                  </Button>
                </HoverHintContent>
              </CardHeader>
              <CardBody className="space-y-2">
                <MrpSkuHoverPeek
                  sku={context.sku}
                  tab="properties"
                  hint={skuPeekHints.skuHoverHints?.[context.sku.trim()]}
                  hintsLoading={skuPeekHints.hintsLoading}
                  onHoverIntent={skuPeekHints.enableHintsFetch}
                  linkClassName="text-lg font-semibold text-sky-700 hover:text-sky-900"
                />
                <WarehouseRecountLocationPanel
                  warehouseLocations={locationsQuery.data ?? []}
                  skuLocations={context.sku_locations}
                  hubLocationId={effectiveHubLocationId}
                  mpnBreakdown={selectedBreakdown}
                  siteDetailLoading={siteDetailLoading}
                  showNonPrimarySites={showNonPrimarySites}
                  disabled={busy}
                  onShowNonPrimarySitesChange={setShowNonPrimarySites}
                  onHubLocationIdChange={handleHubLocationChange}
                  onContainerSelect={(boxHex) => {
                    void selectRecountBox(boxHex);
                  }}
                />
                {warehouseSelected && selectedWarehouseMegaventoryLocationId == null ? (
                  <p className="text-sm text-slate-600">
                    This warehouse has no Megaventory location id, so a location tag cannot be printed.
                  </p>
                ) : null}
              </CardBody>
            </Card>

            {context.box == null && boxHex == null ? (
              <Card>
                <CardHeader className="flex h-14 flex-nowrap items-center justify-between gap-2 py-0">
                  <CardTitleWithHelp hoverHint="Creates a new scannable container. Quantity within unclaimed bulk does not change on-hand. Quantity above unclaimed inventory at SGS opens an inventory discrepancy for the extra and still prints the label.">
                    Carve container
                  </CardTitleWithHelp>
                  {warehouseSelected && carveAtSgs && context.unclaimed <= 0 ? (
                    <p className="truncate text-sm font-semibold text-red-600">Unclaimed inventory is 0</p>
                  ) : null}
                </CardHeader>
                <CardBody className={cn(!warehouseSelected && "pointer-events-none opacity-60")}>
                  <form
                    className="space-y-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (labelSaveSku == null || !carveQuantityValid || !carveMpnReady || carveFormDisabled) {
                        return;
                      }
                      carveMutation.mutate();
                    }}
                  >
                  {warehouseSelected && carveStatusMessage !== "" ? (
                    <p className="text-sm text-slate-600">{carveStatusMessage}</p>
                  ) : null}
                  <div className="grid items-start gap-4 sm:grid-cols-2">
                    <InventoryBoxUnitKindSelect
                      id="warehouse-recount-carve-unit-kind"
                      label="Container type"
                      value={unitKindDraft}
                      disabled={carveFormDisabled}
                      onChange={setUnitKindDraft}
                    />
                    <InventoryAmlMpnEntry
                      sku={context.sku}
                      alternates={context.aml_mpn_alternates}
                      selectedApprovedComponentId={approvedComponentIdDraft}
                      requireExplicitSelection
                      field
                      disabled={carveFormDisabled}
                      onSelect={(approvedComponentId) => {
                        setApprovedComponentIdDraft(approvedComponentId);
                      }}
                    />
                  </div>
                  <div className="grid items-start gap-4 sm:grid-cols-2">
                    <WarehouseRecountShelfField
                      id="warehouse-recount-carve-shelf"
                      shelfDraft={shelfDraft}
                      shelfSublocations={context.shelf_sublocations ?? []}
                      showList={false}
                      disabled={carveFormDisabled}
                      onShelfDraftChange={setShelfDraft}
                    />
                    <div>
                      <Label htmlFor="warehouse-recount-carve-quantity">Container quantity</Label>
                      <Input
                        id="warehouse-recount-carve-quantity"
                        className="mt-1"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={carveQuantityDraft}
                        disabled={carveFormDisabled}
                        onChange={(event) => setCarveQuantityDraft(event.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-slate-900">
                      Other containers on a shelf at this warehouse
                    </h3>
                    <WarehouseRecountShelfList
                      shelfDraft={shelfDraft}
                      shelfSublocations={context.shelf_sublocations ?? []}
                      disabled={carveFormDisabled}
                      onShelfDraftChange={setShelfDraft}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={labelSaveSku == null || !carveQuantityValid || !carveMpnReady || carveFormDisabled}
                  >
                    Carve container and print label
                  </Button>
                  </form>
                </CardBody>
              </Card>
            ) : (
              <Card>
                <CardHeader className="flex h-14 flex-nowrap items-center justify-between gap-2 py-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <CardTitleWithHelp
                      className="min-w-0"
                      hoverHint="Saving updates container quantity, type, shelf, or manufacturer part number. MPN must be on the approved vendor list for this CPN. A quantity change posts a Megaventory adjustment."
                    >
                      Container {boxShortCode ?? context.box?.short_code ?? boxHex?.slice(0, 8).toUpperCase()}
                    </CardTitleWithHelp>
                    {(context.box_badges ?? []).map((badge) => (
                      <Badge
                        key={`${badge.kind}:${badge.label}`}
                        tone={
                          badge.kind === "holding"
                            ? "warning"
                            : badge.kind === "to_stock"
                              ? "info"
                              : "neutral"
                        }
                      >
                        {badge.label}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-9 shrink-0"
                      disabled={!canReprintContainerLabel || busy}
                      onClick={() => reprintContainerMutation.mutate()}
                    >
                      Reprint container label
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-9 w-9 shrink-0 px-0"
                      disabled={busy}
                      aria-label="Clear container"
                      onClick={() => {
                        setBoxHex(null);
                        setBoxShortCode(null);
                      }}
                    >
                      <X className="h-4 w-4" aria-hidden />
                    </Button>
                  </div>
                </CardHeader>
                <CardBody className="space-y-4">
                  <div className="grid items-start gap-4 sm:grid-cols-2">
                    <InventoryBoxUnitKindSelect
                      id="warehouse-recount-box-unit-kind"
                      label="Container type"
                      value={unitKindDraft}
                      disabled={busy}
                      onChange={(unitKind) => {
                        setUnitKindDraft(unitKind);
                        const quantity = context.box?.quantity;
                        if (quantity == null || unitKind === context.box?.unit_kind) return;
                        boxRecountMutation.mutate({ quantity, unitKind });
                      }}
                    />
                    <InventoryAmlMpnEntry
                      sku={context.sku}
                      alternates={context.aml_mpn_alternates}
                      selectedApprovedComponentId={approvedComponentIdDraft}
                      savedApprovedComponentId={context.box?.approved_component_id ?? null}
                      field
                      disabled={busy}
                      onSelect={commitContainerMpn}
                    />
                  </div>
                  <div className="grid items-start gap-4 sm:grid-cols-2">
                    <WarehouseRecountShelfField
                      id="warehouse-recount-box-shelf"
                      shelfDraft={shelfDraft}
                      shelfSublocations={context.shelf_sublocations ?? []}
                      showList={false}
                      disabled={busy || effectiveHubLocationId == null}
                      onShelfDraftChange={setShelfDraft}
                      onCommit={commitContainerShelf}
                    />
                    <div>
                      <Label htmlFor="warehouse-recount-box-quantity">Container quantity</Label>
                      <div className="mt-1 flex items-center gap-2">
                        <Input
                          id="warehouse-recount-box-quantity"
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          value={boxQuantityDraft}
                          disabled
                          readOnly
                        />
                        <Button
                          type="button"
                          className="shrink-0"
                          disabled={busy || effectiveHubLocationId == null || boxHex == null}
                          onClick={() => {
                            setRecountQuantityDraft(boxQuantityDraft);
                            setRecountReason("");
                            setRecountOpen(true);
                          }}
                        >
                          Recount
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-slate-900">
                      Other containers on a shelf at this warehouse
                    </h3>
                    <WarehouseRecountShelfList
                      shelfDraft={shelfDraft}
                      shelfSublocations={context.shelf_sublocations ?? []}
                      disabled={busy || effectiveHubLocationId == null}
                      onShelfDraftChange={setShelfDraft}
                      onCommit={commitContainerShelf}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {context.box?.archived_at == null ? (
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => {
                            void openTransfer("split");
                          }}
                        >
                          Split
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => {
                            void openTransfer("combine");
                          }}
                        >
                          Combine
                        </Button>
                        <Button
                          type="button"
                          variant={cartonAction === "consume" ? "primary" : "secondary"}
                          disabled={busy}
                          onClick={() => {
                            void openCartonAction("consume");
                          }}
                        >
                          Consume
                        </Button>
                        <Button
                          type="button"
                          variant={cartonAction === "release" ? "primary" : "secondary"}
                          disabled={busy}
                          onClick={() => {
                            void openCartonAction("release");
                          }}
                        >
                          Release to floor
                        </Button>
                      </>
                    ) : null}
                  </div>
                  <div className="border-t border-slate-200 pt-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900"
                      aria-expanded={historyOpen}
                      onClick={() => setHistoryOpen((open) => !open)}
                    >
                      <ChevronRight
                        className={`h-4 w-4 transition-transform ${historyOpen ? "rotate-90" : ""}`}
                        aria-hidden
                      />
                      History
                    </button>
                    {historyOpen ? (
                      <div className="mt-2">
                        <ContainerEventTable
                          loading={containerEventsQuery.isFetching}
                          events={containerEventsQuery.data ?? []}
                        />
                      </div>
                    ) : null}
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
          {cartonAction != null && cartonActionBox != null
            ? createPortal(
                <div {...hubModalOverlayProps(true)}>
                  <div
                    className="mt-2 w-full max-w-lg"
                    onPointerDown={(event) => event.stopPropagation()}
                  >
                    {cartonAction === "consume" ? (
                      <InventoryBoxConsumeCard
                        box={cartonActionBox}
                        disabled={recountBusy}
                        onChanged={() => {
                          void baseContextQuery.refetch();
                          if (hubLocationId != null) {
                            void siteContextQuery.refetch();
                          }
                        }}
                        onPendingChange={setCartonActionPending}
                        onCancel={() => {
                          setCartonAction(null);
                          setCartonActionBox(null);
                        }}
                        onCompleted={(nextBox) => {
                          setCartonAction(null);
                          setCartonActionBox(null);
                          if (nextBox.archived_at != null || nextBox.quantity <= 0) {
                            setBoxHex(null);
                            setBoxShortCode(null);
                          }
                        }}
                      />
                    ) : (
                      <InventoryBoxReleaseToFloorCard
                        box={cartonActionBox}
                        disabled={recountBusy}
                        onChanged={() => {
                          void baseContextQuery.refetch();
                          if (hubLocationId != null) {
                            void siteContextQuery.refetch();
                          }
                        }}
                        onPendingChange={setCartonActionPending}
                        onCancel={() => {
                          setCartonAction(null);
                          setCartonActionBox(null);
                        }}
                        onCompleted={() => {
                          setCartonAction(null);
                          setCartonActionBox(null);
                          setBoxHex(null);
                          setBoxShortCode(null);
                        }}
                      />
                    )}
                  </div>
                </div>,
                document.body,
              )
            : null}
          {recountOpen
            ? createPortal(
                <div {...hubModalOverlayProps(true)}>
                  <div
                    className="mt-2 w-full max-w-4xl rounded-lg border border-slate-200 bg-white p-4 shadow-xl"
                    onPointerDown={(event) => event.stopPropagation()}
                  >
                    <h2 className="text-base font-semibold text-slate-900">Recount</h2>
                    <div className="mt-3 space-y-3">
                      <div>
                        <Label htmlFor="warehouse-recount-modal-quantity">Recount quantity</Label>
                        <Input
                          id="warehouse-recount-modal-quantity"
                          className="mt-1"
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          value={recountQuantityDraft}
                          disabled={boxRecountMutation.isPending}
                          onChange={(event) => setRecountQuantityDraft(event.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="warehouse-recount-modal-reason">Reason</Label>
                        <Input
                          id="warehouse-recount-modal-reason"
                          className="mt-1"
                          type="text"
                          autoComplete="off"
                          value={recountReason}
                          disabled={boxRecountMutation.isPending}
                          onChange={(event) => setRecountReason(event.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-slate-900">History</h3>
                        <ContainerEventTable
                          loading={containerEventsQuery.isFetching}
                          events={containerEventsQuery.data ?? []}
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          disabled={
                            boxRecountMutation.isPending
                            || recountReason.trim() === ""
                            || !Number.isFinite(Number.parseInt(recountQuantityDraft.trim(), 10))
                            || Number.parseInt(recountQuantityDraft.trim(), 10) < 0
                          }
                          onClick={() => {
                            const quantity = Number.parseInt(recountQuantityDraft.trim(), 10);
                            boxRecountMutation.mutate({ quantity, reason: recountReason.trim() });
                          }}
                        >
                          Recount
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={boxRecountMutation.isPending}
                          onClick={() => setRecountOpen(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>,
                document.body,
              )
            : null}
          {transferBox != null && transferMode != null ? (
            <InventoryBoxSplitCombineModal
              open
              mode={transferMode}
              boxHex={transferBox.hex}
              boxShortCode={transferBox.short_code}
              unitKind={transferBox.unit_kind}
              quantity={transferBox.quantity}
              selectedPrinter={selectedPrinter}
              onStationChange={setStation}
              station={station}
              unavailableMessage={unavailableMessage}
              onClose={() => {
                setTransferMode(null);
                setTransferBox(null);
              }}
              onChanged={() => {
                void baseContextQuery.refetch();
                if (hubLocationId != null) {
                  void siteContextQuery.refetch();
                }
              }}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
